/**
 * Custom hook for managing address state in User component
 * Consolidates multiple address-related states into a cohesive structure
 */

import { useState, useCallback, useMemo } from "react";
import * as intrfce from "./Interfaces";

/**
 * Interface for consolidated address state
 */
export interface AddressState {
  // Billing
  billing: {
    selected: intrfce.iSavedAddress | null | "reset";
    saved: intrfce.iSavedAddress[];
    form: intrfce.iAddressFormData;
    contact: intrfce.iBillingFormData;
    readonly: boolean;
  };
  // Shipping
  shipping: {
    selected: intrfce.iSavedAddress | null | "reset";
    saved: intrfce.iSavedAddress[];
    form: intrfce.iAddressFormData;
    contact: {
      firstName: string;
      lastName: string;
      phone: string;
    };
    readonly: boolean;
    enabled: boolean; // Whether shipping is different from billing
  };
  // Modal-related
  modal: {
    temporary: intrfce.iSavedAddress | null;
    recommended: intrfce.iAddressFormData | null;
    selectedOption: "yourAddress" | "recommendedAddress";
  };
}

/**
 * Custom hook for managing address state
 */
export const useAddressState = (
  countryList: Array<{ countryName: string; ISO_CC: string }>,
  locale: string
) => {
  const [addressState, setAddressState] = useState<AddressState>({
    billing: {
      selected: null,
      saved: [],
      form: {
        pincode: "",
        address: "",
        line2: "",
        cityValue: "",
        stateValue: locale === 'zh-tw' ? "TW" : "",
        countryName: countryList[0]?.countryName || "",
        countryValue: countryList[0]?.ISO_CC || "",
      },
      contact: {
        firstNameBilling: "",
        lastNameBilling: "",
        contactBilling: "",
        emailBilling: "",
        vemailBilling: ""
      },
      readonly: false,
    },
    shipping: {
      selected: null,
      saved: [],
      form: {
        pincode: "",
        address: "",
        line2: "",
        cityValue: "",
        stateValue: locale === 'zh-tw' ? "TW" : "",
        countryName: countryList[0]?.countryName || "",
        countryValue: countryList[0]?.ISO_CC || "",
      },
      contact: {
        firstName: "",
        lastName: "",
        phone: "",
      },
      readonly: false,
      enabled: false,
    },
    modal: {
      temporary: null,
      recommended: null,
      selectedOption: "yourAddress",
    },
  });

  // Generic setter factory - supports both direct values and functional updates
  const createSetter = useCallback(<T,>(
    section: 'billing' | 'shipping' | 'modal',
    field: string
  ) => {
    return (value: T | ((prev: T) => T)) => {
      if (typeof value === 'function') {
        setAddressState(prev => {
          const sectionData = prev[section] as any;
          return {
            ...prev,
            [section]: {
              ...sectionData,
              [field]: (value as Function)(sectionData[field])
            }
          };
        });
      } else {
        setAddressState(prev => ({
          ...prev,
          [section]: { ...(prev[section] as any), [field]: value }
        }));
      }
    };
  }, []);

  // Billing setters - createSetter already returns memoized functions
  const setSelectedBillingAddress = createSetter<intrfce.iSavedAddress | null | "reset">('billing', 'selected');
  const setSavedBillingAddress = createSetter<intrfce.iSavedAddress[]>('billing', 'saved');
  const setFormBillingAddress = createSetter<intrfce.iAddressFormData>('billing', 'form');
  const setFormBillingData = createSetter<intrfce.iBillingFormData>('billing', 'contact');
  const setIsBillingReadonly = createSetter<boolean>('billing', 'readonly');

  // Shipping setters
  const setSelectedShippingAddress = createSetter<intrfce.iSavedAddress | null | "reset">('shipping', 'selected');
  const setSavedShippingAddress = createSetter<intrfce.iSavedAddress[]>('shipping', 'saved');
  const setFormShippingAddress = createSetter<intrfce.iAddressFormData>('shipping', 'form');
  const setIsShippingReadonly = createSetter<boolean>('shipping', 'readonly');
  const setIsShipping = createSetter<boolean>('shipping', 'enabled');

  // Modal setters
  const setTemporaryAddress = createSetter<intrfce.iSavedAddress | null>('modal', 'temporary');
  const setRecommendedShippingAddress = createSetter<intrfce.iAddressFormData | null>('modal', 'recommended');
  const setSelectedAddressOption = useCallback(
    (option: string) => createSetter<"yourAddress" | "recommendedAddress">('modal', 'selectedOption')(option as "yourAddress" | "recommendedAddress"),
    [createSetter]
  );

  // Memoize form objects to prevent unnecessary rerenders
  // Only create new references when the actual data changes
  const memoizedFormBillingAddress = useMemo(
    () => addressState.billing.form,
    [ // eslint-disable-line react-hooks/exhaustive-deps
      addressState.billing.form.pincode,
      addressState.billing.form.address,
      addressState.billing.form.line2,
      addressState.billing.form.cityValue,
      addressState.billing.form.stateValue,
      addressState.billing.form.countryName,
      addressState.billing.form.countryValue,
    ]
  );

  const memoizedFormBillingData = useMemo(
    () => addressState.billing.contact,
    [ // eslint-disable-line react-hooks/exhaustive-deps
      addressState.billing.contact.firstNameBilling,
      addressState.billing.contact.lastNameBilling,
      addressState.billing.contact.contactBilling,
      addressState.billing.contact.emailBilling,
      addressState.billing.contact.vemailBilling,
    ]
  );

  const memoizedFormShippingAddress = useMemo(
    () => addressState.shipping.form,
    [ // eslint-disable-line react-hooks/exhaustive-deps
      addressState.shipping.form.pincode,
      addressState.shipping.form.address,
      addressState.shipping.form.line2,
      addressState.shipping.form.cityValue,
      addressState.shipping.form.stateValue,
      addressState.shipping.form.countryName,
      addressState.shipping.form.countryValue,
    ]
  );

  const memoizedSavedBillingAddress = useMemo(
    () => addressState.billing.saved,
    [JSON.stringify(addressState.billing.saved)] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const memoizedSavedShippingAddress = useMemo(
    () => addressState.shipping.saved,
    [JSON.stringify(addressState.shipping.saved)] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const memoizedRecommendedShippingAddress = useMemo(
    () => addressState.modal.recommended,
    [JSON.stringify(addressState.modal.recommended)] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    // Billing state
    selectedBillingAddress: addressState.billing.selected,
    setSelectedBillingAddress,
    savedBillingAddress: memoizedSavedBillingAddress,
    setSavedBillingAddress,
    formBillingAddress: memoizedFormBillingAddress,
    setFormBillingAddress,
    formBillingData: memoizedFormBillingData,
    setFormBillingData,
    isBillingReadonly: addressState.billing.readonly,
    setIsBillingReadonly,

    // Shipping state
    selectedShippingAddress: addressState.shipping.selected,
    setSelectedShippingAddress,
    savedShippingAddress: memoizedSavedShippingAddress,
    setSavedShippingAddress,
    formShippingAddress: memoizedFormShippingAddress,
    setFormShippingAddress,
    isShippingReadonly: addressState.shipping.readonly,
    setIsShippingReadonly,
    isShipping: addressState.shipping.enabled,
    setIsShipping,

    // Modal state
    temporaryAddress: addressState.modal.temporary,
    setTemporaryAddress,
    recommendedShippingAddress: memoizedRecommendedShippingAddress,
    setRecommendedShippingAddress,
    selectedAddressOption: addressState.modal.selectedOption,
    setSelectedAddressOption,
  };
};

